import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector, useDispatch } from 'react-redux';
import Helmet from 'react-helmet';
import _isEmpty from 'lodash/isEmpty';

import { getLocalStorage } from '@zola-helpers/client';

import CardsModalRoot from 'cards/components/common/CardsModalRoot';
import CustomizableBanner from 'components/manage/website/WebsiteCustomization/components/CustomizableBanner';
import { getUserContext } from 'selectors/user/userSelectors';
import { selectDefaultValues } from 'selectors/website/customizationSelectors';
import { getDashboardTheme } from 'actions/WeddingThemeActions';

import experimentFlags, { WEBSITE_CUSTOMIZATION_ENABLED } from '../../../util/experimentFlags';

// virtual event tour
import {
  localStorageKey,
  STEPS,
} from './WalkthroughTour/StepCollections/VirtualEventsOnboardingEditPage';
import WalkthroughTour from './WalkthroughTour/WalkthroughTour';

// Actions
import { activatePage } from '../../../actions/website/PageActions';

// Utils
import { EDIT_PAGE_COMPONENTS } from './util/mapPageTypeToComponents';

const EditPage = ({ pageKey, page, activateCmsPage, theme, userContext }) => {
  const pageType = page ? page.type : undefined;
  const CurrentPage = pageType ? EDIT_PAGE_COMPONENTS[pageType].page : undefined;
  const CurrentPreview = pageType ? EDIT_PAGE_COMPONENTS[pageType].preview : undefined;
  const themeKey = theme.key || undefined;
  const themeFont = theme.font_name || undefined;
  const isDev =
    window.location.hostname === 'localhost' || window.location.hostname === 'qa.zola.com';
  const cloudfront = isDev
    ? 'https://d2wzwlp3rxwsn1.cloudfront.net'
    : 'https://d2gt2lt628wbyx.cloudfront.net';
  const helmetCssLink = [
    ...(themeFont
      ? [
          {
            rel: 'stylesheet',
            href: `https://fonts.googleapis.com/css?family=${themeFont}`,
          },
        ]
      : []),
    ...(themeKey
      ? [
          {
            rel: 'stylesheet',
            href: `${cloudfront}/${themeKey}.css`,
          },
        ]
      : []),
  ];

  useEffect(() => {
    if (pageKey) activateCmsPage(pageKey);
  }, [pageKey, activateCmsPage, page]);

  // walkthrough tour
  const isTourDisabled = getLocalStorage(localStorageKey) === 'true';
  const showVirtualEventTour = pageType === 'EVENT' && !isTourDisabled;

  /* Website Customization */
  const websiteCustomizationEnabled = experimentFlags.getFlag(
    WEBSITE_CUSTOMIZATION_ENABLED,
    userContext
  );
  const dispatch = useDispatch();
  const customizationDefaults = useSelector(selectDefaultValues);
  useEffect(() => {
    if (websiteCustomizationEnabled) {
      dispatch(getDashboardTheme());
    }
  }, []);

  const isCustomizable = !_isEmpty(customizationDefaults);
  const showCustomizableBanner =
    websiteCustomizationEnabled && isCustomizable && pageKey === 'HOME';

  return (
    <>
      {themeKey && <Helmet link={helmetCssLink} />}
      {page && (
        <>
          {!CurrentPreview ? (
            <CurrentPage page={page} pageType={pageType} themeKey={themeKey} />
          ) : (
            <div className="container-fluid">
              <div className="row">
                <div className="column col-xs-12 col-md-5" id="sticky-preview-anchor">
                  <CurrentPage useDragHandle lockAxis="y" />
                </div>
                <div className="column col-md-7 hidden-sm hidden-xs">
                  {showCustomizableBanner && <CustomizableBanner />}
                  <CurrentPreview pageType={pageType} />
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {showVirtualEventTour && (
        <WalkthroughTour steps={STEPS} localStorageKey={localStorageKey} desktopOnly />
      )}
      <CardsModalRoot />
    </>
  );
};

EditPage.propTypes = {
  page: PropTypes.shape({}),
  activateCmsPage: PropTypes.func.isRequired,
  pageKey: PropTypes.string,
  theme: PropTypes.shape({ key: PropTypes.string, font_name: PropTypes.string }),
  userContext: PropTypes.shape({}),
};

const mapStateToProps = (state, ownProps) => {
  const { byId, typeToId, orderedIds } = state.website.pages;
  const pageKey = ownProps.params.pageKey ? ownProps.params.pageKey.toUpperCase() : undefined;
  const pageId = pageKey ? typeToId[pageKey] : undefined;
  const page = pageId ? byId[pageId] : byId[orderedIds[0]];

  return {
    pageKey,
    page,
    theme: state.wedding.theme,
    userContext: getUserContext(state),
  };
};

const mapDispatchToProps = dispatch => ({
  activateCmsPage: pageKey => dispatch(activatePage(pageKey)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditPage);
